/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Monday, 9th Sept 2019
 *
 *  Location Users View
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { observer } from 'mobx-react';
import MaterialTable from 'material-table';
import { useStores } from 'stores/RootStore';
import { getFormattedDate } from 'utils';

import UserInvite from 'components/Dialogs/UserInvite';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import Avatar from '@material-ui/core/Avatar';
import PersonIcon from '@material-ui/icons/PersonRounded';
import AddIcon from '@material-ui/icons/AddBox';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import EmailIcon from '@material-ui/icons/Email';
import DetailPanelIcon from '@material-ui/icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import ExportIcon from '@material-ui/icons/SaveAlt';
import FilterIcon from '@material-ui/icons/FilterList';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NextPageIcon from '@material-ui/icons/ChevronRight';
import PreviousPageIcon from '@material-ui/icons/ChevronLeft';
import ResetSearchIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import SortArrowIcon from '@material-ui/icons/ArrowDownward';
import ThirdStateCheckIcon from '@material-ui/icons/Remove';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

const rolesEnum = { Admin: 'Admin', User: 'User' };

const Users = props => {
  const { classes, theme } = props;
  const [open, setOpen] = React.useState(false);
  const stores = useStores();
  const {
    isUserLoading,
    isUserInvitesLoading,
    locationUsers,
    userInvites,
    isAdmin,
    locationId,
    updateUser,
    handleRemoveInvite,
    handleSendInvite,
  } = stores.locationStore;

  // Update AppStore Page View
  stores.appStore.setView('Users');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const columns = [
    {
      field: 'user.uid',
      hidden: true,
      editable: 'never',
      removable: false,
    },
    {
      sorting: false,
      editable: 'never',
      removable: false,
      hidden: isWidthDown('xs', props.width),
      render: rowData =>
        rowData.avatarUrl !== undefined && rowData.avatarUrl !== '' && rowData.avatarUrl !== null ? (
          <Avatar alt='Avatar' src={rowData.avatarUrl} className={classes.avatar} />
        ) : rowData.initials !== '' ? (
          <Avatar alt='Avatar' className={classes.avatarInitials}>
            {rowData.initials}
          </Avatar>
        ) : (
          <PersonIcon color='inherit' className={classes.avatarIcon} />
        ),
      cellStyle: {
        margin: '0',
        width: '40px',
        paddingLeft: '10px',
        paddingRight: '5px',
      },
    },
    {
      field: 'displayName',
      title: 'Name',
      editable: 'never',
      removable: false,
      defaultSort: 'asc',
      sorting: false,
      cellStyle: {
        paddingLeft: '5px',
      },
    },
    {
      field: 'email',
      title: 'Email',
      editable: 'never',
      removable: false,
      sorting: false,
      hidden: isWidthDown('sm', props.width),
    },
    {
      field: 'active',
      title: 'Active',
      editable: isAdmin ? 'onUpdate' : 'never',
      removable: false,
      sorting: false,
      type: 'boolean',
    },
    {
      field: 'role',
      title: 'Role',
      editable: isAdmin ? 'onUpdate' : 'never',
      removable: false,
      sorting: false,
      lookup: rolesEnum,
    },
  ];

  const invitesColumns = [
    {
      field: 'id',
      hidden: true,
      editable: 'never',
      removable: false,
    },
    {
      field: 'email',
      title: 'Email',
      sorting: false,
      removable: false,
    },
    {
      field: 'timestamp',
      title: 'Sent',
      render: rowData => getFormattedDate(rowData.timestamp),
      sorting: false,
      type: 'datetime',
      removable: false,
      hidden: !isAdmin || isWidthDown('xs', props.width),
    },
    {
      field: 'invitedBy.email',
      title: 'Invited By',
      sorting: false,
      removable: false,
      hidden: !isAdmin || isWidthDown('sm', props.width),
    },
  ];

  let tableEditable = {};
  if (isAdmin) {
    tableEditable = {
      editable: {
        onRowUpdate: (newData, oldData) => updateUser(newData, oldData),
      },
    };
  }

  return (
    <Grid container direction='column'>
      <Grid item xs={12} md={11} lg={9}>
        <Grid container className={classes.gridContainer}>
          <Grid item xs={12}>
            <MaterialTable
              isLoading={isUserLoading}
              data={locationUsers}
              style={{
                boxShadow: 'none',
                backgroundColor: 'inherit',
              }}
              {...tableEditable}
              columns={columns}
              icons={{
                Add: React.forwardRef((props, ref) => <AddIcon {...props} className={classes.icon} ref={ref} />),
                Check: React.forwardRef((props, ref) => <CheckIcon {...props} className={classes.icon} ref={ref} />),
                Clear: React.forwardRef((props, ref) => <ClearIcon {...props} className={classes.icon} ref={ref} />),
                Delete: React.forwardRef((props, ref) => <DeleteIcon {...props} className={classes.icon} ref={ref} />),
                DetailPanel: React.forwardRef((props, ref) => (
                  <DetailPanelIcon {...props} className={classes.icon} ref={ref} />
                )),
                Edit: React.forwardRef((props, ref) => <EditIcon {...props} className={classes.icon} ref={ref} />),
                Export: React.forwardRef((props, ref) => <ExportIcon {...props} className={classes.icon} ref={ref} />),
                Filter: React.forwardRef((props, ref) => <FilterIcon {...props} className={classes.icon} ref={ref} />),
                FirstPage: React.forwardRef((props, ref) => (
                  <FirstPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                LastPage: React.forwardRef((props, ref) => (
                  <LastPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                NextPage: React.forwardRef((props, ref) => (
                  <NextPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                PreviousPage: React.forwardRef((props, ref) => (
                  <PreviousPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                ResetSearch: React.forwardRef((props, ref) => (
                  <ResetSearchIcon {...props} className={classes.icon} ref={ref} />
                )),
                Search: React.forwardRef((props, ref) => <SearchIcon {...props} className={classes.icon} ref={ref} />),
                SortArrow: React.forwardRef((props, ref) => (
                  <SortArrowIcon {...props} className={classes.icon} ref={ref} />
                )),
                ThirdStateCheck: React.forwardRef((props, ref) => (
                  <ThirdStateCheckIcon {...props} className={classes.icon} ref={ref} />
                )),
                ViewColumn: React.forwardRef((props, ref) => (
                  <ViewColumnIcon {...props} className={classes.icon} ref={ref} />
                )),
              }}
              options={{
                loadingType: 'linear',
                padding: 'dense',
                search: false,
                toolbar: false,
                showTitle: false,
                header: true,
                paging: locationUsers.length > 5,
                emptyRowsWhenPaging: false,
                pageSize: 5,
                pageSizeOptions: [5, 10, 25, 50],
                actionsColumnIndex: -1,
                headerStyle: {
                  backgroundColor: theme.palette.background.default,
                  padding: '5px',
                  paddingLeft: '15px',
                },
                rowStyle: { backgroundColor: theme.palette.background.default },
              }}
              localization={{
                header: {
                  actions: '',
                },
                body: {
                  emptyDataSourceMessage: 'No users to display',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} className={classes.invitesWrapper}>
            <Grid
              container
              direction='row'
              justify='space-between'
              alignItems='center'
              className={classes.invitesHeader}
            >
              <Grid item>
                <Typography variant='subtitle1'>Pending Invitations</Typography>
              </Grid>
              <Grid item>
                {isAdmin || false ? (
                  <Button color='primary' variant='outlined' size='small' onClick={handleClickOpen}>
                    Invite User
                  </Button>
                ) : null}
              </Grid>
            </Grid>
            <MaterialTable
              isLoading={isUserInvitesLoading}
              data={userInvites}
              columns={invitesColumns}
              style={{
                boxShadow: 'none',
                backgroundColor: 'inherit',
              }}
              icons={{
                Add: React.forwardRef((props, ref) => <AddIcon {...props} className={classes.icon} ref={ref} />),
                Check: React.forwardRef((props, ref) => <CheckIcon {...props} className={classes.icon} ref={ref} />),
                Clear: React.forwardRef((props, ref) => <ClearIcon {...props} className={classes.icon} ref={ref} />),
                Delete: React.forwardRef((props, ref) => <DeleteIcon {...props} className={classes.icon} ref={ref} />),
                DetailPanel: React.forwardRef((props, ref) => (
                  <DetailPanelIcon {...props} className={classes.icon} ref={ref} />
                )),
                Edit: React.forwardRef((props, ref) => <EditIcon {...props} className={classes.icon} ref={ref} />),
                Export: React.forwardRef((props, ref) => <ExportIcon {...props} className={classes.icon} ref={ref} />),
                Filter: React.forwardRef((props, ref) => <FilterIcon {...props} className={classes.icon} ref={ref} />),
                FirstPage: React.forwardRef((props, ref) => (
                  <FirstPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                LastPage: React.forwardRef((props, ref) => (
                  <LastPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                NextPage: React.forwardRef((props, ref) => (
                  <NextPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                PreviousPage: React.forwardRef((props, ref) => (
                  <PreviousPageIcon {...props} className={classes.icon} ref={ref} />
                )),
                ResetSearch: React.forwardRef((props, ref) => (
                  <ResetSearchIcon {...props} className={classes.icon} ref={ref} />
                )),
                Search: React.forwardRef((props, ref) => <SearchIcon {...props} className={classes.icon} ref={ref} />),
                SortArrow: React.forwardRef((props, ref) => (
                  <SortArrowIcon {...props} className={classes.icon} ref={ref} />
                )),
                ThirdStateCheck: React.forwardRef((props, ref) => (
                  <ThirdStateCheckIcon {...props} className={classes.icon} ref={ref} />
                )),
                ViewColumn: React.forwardRef((props, ref) => (
                  <ViewColumnIcon {...props} className={classes.icon} ref={ref} />
                )),
              }}
              options={{
                padding: 'dense',
                loadingType: 'linear',
                search: false,
                toolbar: false,
                showTitle: false,
                header: true,
                paging: true,
                emptyRowsWhenPaging: false,
                pageSize: 10,
                pageSizeOptions: [10, 25, 50],
                actionsColumnIndex: -1,
                headerStyle: {
                  backgroundColor: theme.palette.background.default,
                  padding: '5px',
                  paddingLeft: '15px',
                },
                rowStyle: { backgroundColor: theme.palette.background.default },
              }}
              actions={[
                {
                  icon: () => <EmailIcon className={classes.icon} />,
                  tooltip: 'Resend Invite Email',
                  hidden: !isAdmin,
                  onClick: (event, rowData) => handleSendInvite(event, rowData),
                },
                {
                  icon: () => <DeleteIcon className={classes.icon} />,
                  tooltip: 'Remove Invite',
                  onClick: (event, rowData) => handleRemoveInvite(event, rowData),
                  hidden: !isAdmin,
                },
              ]}
              localization={{
                header: {
                  actions: '',
                },
                body: {
                  emptyDataSourceMessage: 'No invites to display',
                  editTooltip: 'Edit',
                },
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <UserInvite open={open} handleClose={handleClose} type={'location'} id={locationId || ''} />
    </Grid>
  );
};

Users.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  gridContainer: {
    paddingBottom: '40px',
  },
  icon: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
  },
  avatar: {},
  avatarInitials: {},
  avatarIcon: {},
  invitesWrapper: {
    marginTop: '50px',
  },
  invitesHeader: {
    padding: '10px',
    color: theme.palette.text.primary,
  },
  invitesButton: {},
});

export default withStyles(styles, { withTheme: true })(withWidth()(observer(Users)));
